import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          const API_BASE = process.env.REACT_APP_API_URL || '/api';
          const response = await axios.get(`${API_BASE}/auth/profile`);
          const latestUser = response.data.user;
          setUser(latestUser);
          localStorage.setItem('user', JSON.stringify(latestUser));
        } catch (error) {
          console.error('Failed to sync user profile:', error);
          if (storedUser) {
            setUser(JSON.parse(storedUser));
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            delete axios.defaults.headers.common['Authorization'];
          }
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const response = await axios.post(`${API_BASE}/auth/login`, {
        email,
        password
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      return { success: true, user };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed',
        locked: error.response?.data?.locked || false,
        lockedUntil: error.response?.data?.lockedUntil || null,
        attemptsRemaining: error.response?.data?.attemptsRemaining
      };
    }
  };

  const sendOtp = async (name, email) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const response = await axios.post(`${API_BASE}/auth/send-otp`, {
        name,
        email: email.toLowerCase().trim()
      });

      if (response.data.otp) {
        console.log(
          `%c[DEVELOPMENT] Your OTP is: ${response.data.otp}`,
          'color: #10b981; font-size: 16px; font-weight: bold;'
        );
      }

      return {
        success: true,
        message: response.data.message,
        otp: response.data.otp || null
      };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Failed to send OTP' 
      };
    }
  };

  const register = async (name, email, password, role, securityQuestion, securityAnswer, otp) => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || '/api';
      const response = await axios.post(`${API_BASE}/auth/register`, {
        name,
        email: email.toLowerCase().trim(),
        password,
        role,
        securityQuestion,
        securityAnswer,
        otp: String(otp).trim()
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);

      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Registration failed' 
      };
    }
  };


  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  const value = {
    user,
    login,
    register,
    sendOtp,
    logout,
    updateUser,
    loading
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};